import React, { FormEvent, useState, useEffect } from 'react';
import { Button, Modal, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';

import '../../Plans/modals/plan_createplan.css';
import { useAuth } from '../../../../../firebase/AuthProvider';
import { CreatePlanFeatureRequest } from '../../../../../interfaces';
import { createPlanFeatureAsync } from '../../../../../services/PlanService/PlanFeatureService';
import { PlanFeatureKey } from '../../../../../Data/SD';
import { AxiosError } from 'axios';
import { useAbortManager } from '../../../../../hooks/cancelletionManagerHook/useAbortManager';

interface CreatePlanFeatureModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: () => void | Promise<void>;
    planId: string;
}

const CreatePlanFeatureModal: React.FC<CreatePlanFeatureModalProps> = ({
    isOpen,
    onClose,
    onSuccess,
    planId,
}) => {
    const cancelletionManager = useAbortManager();
    const { currentUser } = useAuth();
    const [form, setForm] = useState<CreatePlanFeatureRequest>({
        planId: planId,
        featureKey: '',
        isEnabled: true,
        limitValue: null,
        featureName: ''
    });
    const [errors, setErrors] = useState<Partial<Record<keyof CreatePlanFeatureRequest, string>>>({});
    const [isLoading, setIsLoading] = useState<boolean>(false);


    useEffect(() => {
        // reset form whenever planId or modal opens
        setForm({
            planId: planId,
            featureKey: '',
            isEnabled: true,
            limitValue: null,
            featureName: ''
        });
        setErrors({});
    }, [planId, isOpen]);

    const handleInputChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
    ) => {
        const { name, value, type, checked } = e.target as HTMLInputElement;
        setForm((prev) => ({
            ...prev,
            [name]:
                type === 'number'
                    ? value === ''
                        ? null
                        : Number(value)
                    : type === 'checkbox'
                        ? checked
                        : value,
        } as any));
        setErrors((prev) => ({ ...prev, [name]: undefined }));
    };

    const validateForm = () => {
        const newErrors: Partial<Record<keyof CreatePlanFeatureRequest, string>> = {};
        if (!form.featureName || !form.featureName.trim()) {
            newErrors.featureName = 'Please enter a feature key.';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!validateForm()) return;
        if (!currentUser) return;

        const token = await currentUser.getIdToken();
        if (!token) {
            toast.error('Authentication error');
            return;
        }

        setIsLoading(true);
        const controller = cancelletionManager.create("createFeature");
        try {
            const res = await createPlanFeatureAsync(token, form, controller.signal);
            if (res.success) {
                toast.success('Feature created successfully');
                onSuccess && onSuccess();
                onClose();
            }
        } catch (error: any) {

            if (error.name === 'AbortError') return;
            toast.error(error instanceof AxiosError ? error.response?.data.message : error.message || "Error to Save the Feature.");

        } finally {
            setIsLoading(false);
            cancelletionManager.remove("createFeature");
        }
    };

    const handleClose = () => {
        setErrors({});
        onClose();
    };

    return (
        <Modal
            show={isOpen}
            onHide={handleClose}
            className="custom_popup"
            centered
            backdrop={isLoading ? 'static' : true}
            keyboard={!isLoading}
        >
            <form onSubmit={handleSubmit}>
                <Modal.Header closeButton={!isLoading}>
                    <Modal.Title>Add Feature</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="plan_formGroup">
                        <label className="plan_label" htmlFor="featureName">
                            Feature Name
                        </label>
                        <input
                            id="featureName"
                            name="featureName"
                            type="text"
                            placeholder='Enter the Feature Name..'
                            value={form.featureName}
                            onChange={handleInputChange}
                            className="plan_input"
                        />
                        {errors.featureKey && (
                            <div className="text-red-500 text-sm">{errors.featureName}</div>
                        )}
                    </div>

                    <div className="plan_formGroup">
                        <select
                            id="featureKey"
                            name="featureKey"
                            value={form.featureKey ?? ''}
                            onChange={handleInputChange}
                            className="plan_input"
                        >
                            <option value="">Select Feature</option>

                            {Object.values(PlanFeatureKey).map((key) => (
                                <option key={key} value={key}>
                                    {key}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="plan_formGroup flex items-center gap-2">
                        <input
                            id="isEnabled"
                            name="isEnabled"
                            type="checkbox"
                            checked={form.isEnabled}
                            onChange={handleInputChange}
                        />
                        <label className="plan_label" htmlFor="isEnabled">
                            Enabled
                        </label>
                    </div>

                    <div className="plan_formGroup">
                        <label className="plan_label" htmlFor="limitValue">
                            Limit Value
                        </label>
                        <input
                            id="limitValue"
                            name="limitValue"
                            type="number"
                            min="0"
                            value={form.limitValue ?? ''}
                            onChange={handleInputChange}
                            className="plan_input"
                        />
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <button
                        type="button"
                        onClick={handleClose}
                        className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                        disabled={isLoading}
                    >
                        Cancel
                    </button>
                    <Button
                        type="submit"
                        className="px-6 py-2.5 text-sm font-medium rounded-lg bg-[#7E57C2] text-white hover:bg-[#6D4CAB] flex items-center justify-center"
                        disabled={isLoading}
                    >
                        {isLoading ? <Spinner animation="border" size="sm" className="mr-2" /> : 'Create'}
                    </Button>
                </Modal.Footer>
            </form>
        </Modal>
    );
};

export default CreatePlanFeatureModal;
